import i18n from "i18next";
import { initReactI18next } from "react-i18next";
import HttpBackend from "i18next-http-backend";
import { logger } from "@/shared/utils/logger";

/**
 * i18n Configuration
 *
 * Setup internationalization using i18next with HTTP backend for loading
 * translation files. The library (Helios Frames) handles its own translations,
 * while this configuration handles app-specific translations.
 *
 * Translation files are located in: /public/i18n/{lng}.json
 */

// Supported languages
export const SUPPORTED_LANGUAGES = [
  "en-US",
  "es-ES",
  "fr-FR",
  "de-DE",
] as const;
export type SupportedLanguage = (typeof SUPPORTED_LANGUAGES)[number];

// Default language
export const DEFAULT_LANGUAGE: SupportedLanguage = "en-US";

i18n
  .use(HttpBackend)
  .use(initReactI18next)
  .init({
    // Fallback language if detection fails
    fallbackLng: DEFAULT_LANGUAGE,

    // Load only the current language
    load: "currentOnly",

    // Supported languages
    supportedLngs: [...SUPPORTED_LANGUAGES],

    // Backend configuration for loading translation files
    backend: {
      loadPath: "/i18n/{{lng}}.json",
    },

    // Namespace configuration
    ns: ["translation"],
    defaultNS: "translation",

    // Interpolation configuration
    interpolation: {
      escapeValue: false, // React already escapes values
    },

    // Enable debug mode in development
    debug: import.meta.env.DEV,

    // React specific options
    react: {
      useSuspense: true,
    },
  })
  .then(() => {
    logger.info("i18n initialized successfully");
  })
  .catch((error) => {
    logger.error("i18n initialization failed:", error);
  });

// Event listeners for debugging
if (import.meta.env.DEV) {
  i18n.on("loaded", (loaded) => {
    logger.debug("i18n resources loaded:", loaded);
  });

  i18n.on("failedLoading", (lng, ns, msg) => {
    logger.error(`i18n failed loading: ${lng}/${ns} - ${msg}`);
  });

  i18n.on("languageChanged", (lng) => {
    logger.info("Language changed to:", lng);
  });
}

/**
 * Sync document language attribute and direction
 * Call this when language changes
 */
export const syncDocumentLanguage = (language: string): void => {
  document.documentElement.lang = language;
  // Set direction for RTL languages if needed
  document.documentElement.dir = i18n.dir(language);
};

export default i18n;
